import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "../../environments/environment";
import { Wallet } from "@models/wallet";
import { StatementFilters, Transaction } from "@models/transaction";
import { PaginatorState } from "primeng/paginator";
import { Bank } from "@models/bank";
import { BankAccount } from "@models/bank-account";
import { catchError, of, tap } from "rxjs";
import { SnackBarService } from "./snack-bar.service";
import { Page } from "@models/page";

@Injectable({
  providedIn: "root",
})
export class WalletService {
  private readonly baseUrl = `${environment.API_BACKEND}`;

  constructor(
    private readonly http: HttpClient,
    private readonly notificationService: SnackBarService,
  ) {}

  getWallet() {
    return this.http.get<Wallet>(`${this.baseUrl}/wallet`);
  }

  getTransactions(pageEvent?: PaginatorState, filters?: StatementFilters) {
    let params = new HttpParams()
      .set("page", (pageEvent?.page ?? 0) + 1)
      .set("per_page", pageEvent?.rows ?? 10);

    if (filters?.startDate) {
      params = params.set(
        "start_date",
        this.formatDate(filters.startDate),
      );
    }
    if (filters?.endDate) {
      params = params.set("end_date", this.formatDate(filters.endDate));
    }
    if (filters?.eventId) {
      params = params.set("event_id", filters.eventId);
    }
    if (filters?.paymentMethod) {
      params = params.set("payment_method", filters.paymentMethod);
    }
    if (filters?.paymentChannel) {
      params = params.set("payment_channel", filters.paymentChannel);
    }
    if (filters?.direction) {
      params = params.set("direction", filters.direction);
    }

    return this.http
      .get<Page<Transaction>>(`${this.baseUrl}/statements`, { params })
      .pipe(
        catchError(() =>
          of({
            data: [],
            meta: {
              total_count: 0,
              total_pages: 0,
              current_page: 0,
              page_size: 10,
            },
          } as Page<Transaction>),
        ),
      );
  }

  private formatDate(date: Date): string {
    return date.toISOString().split("T")[0];
  }

  getBanks() {
    const bankurl = "/assets/data/banks.json";
    return this.http.get<Bank[]>(`${bankurl}`);
  }

  createBankAccount(bankAccount: BankAccount) {
    return this.http
      .post(`${this.baseUrl}/bank_account`, bankAccount)
      .pipe(
        tap(() =>
          this.notificationService.openSnackBar(
            "Dados bancários cadastrados com sucesso",
            "Fechar",
            "success",
          ),
        ),
      );
  }

  updateBankAccount(bankAccount: BankAccount) {
    return this.http
      .patch(`${this.baseUrl}/bank_account`, bankAccount)
      .pipe(
        tap(() =>
          this.notificationService.openSnackBar(
            "Solicitação de alteração enviada. Acompanhe o status na página de solicitações.",
            "Fechar",
            "success",
          ),
        ),
      );
  }

  getBankAccount() {
    return this.http.get<BankAccount>(`${this.baseUrl}/bank_account`);
  }

  deleteBankAccount() {
    return this.http
      .delete(`${this.baseUrl}/bank_account`)
      .pipe(
        tap(() =>
          this.notificationService.openSnackBar(
            "Dados bancários removidos com sucesso",
            "Fechar",
            "success",
          ),
        ),
      );
  }
}
